import { ref } from 'vue';

export function useServerTable({
                                   initialRows = 10,
                                   debounceMs = 300,
                                   // loadPage: async ({ page, rows, search, ...extra }) => ({ results, count })
                                   loadPage,
                                   storageKey,
                               }) {
    const data = ref([]);
    const totalRecords = ref(0);
    const loading = ref(false);
    const rowsPerPage = ref(initialRows);
    const currentPage = ref(1);
    const search = ref('');
    const extra = ref({});
    const sortField = ref(null);
    const sortOrder = ref(null);
    const allowedRows = [10, 20, 30];

    if (storageKey) {
        const raw = localStorage.getItem(storageKey);
        const parsed = Number(raw);
        if (Number.isFinite(parsed) && allowedRows.includes(parsed)) {
            rowsPerPage.value = parsed;
        }
    }

    function persistRows() {
        if (!storageKey) return;
        if (allowedRows.includes(rowsPerPage.value)) {
            localStorage.setItem(storageKey, String(rowsPerPage.value));
        }
    }

    async function load() {
        loading.value = true;
        try {
            const ordering = sortField.value
                ? (sortOrder.value === -1 ? `-${sortField.value}` : sortField.value)
                : undefined;

            const resp = await loadPage({
                page: currentPage.value,
                rows: rowsPerPage.value,
                search: search.value,
                ...extra.value,
                ...(ordering && { ordering }),
            });
            data.value = resp?.results || [];
            totalRecords.value = Number(resp?.count ?? 0);
            // Кламп, если страница вышла за пределы
            const maxPage = Math.max(1, Math.ceil(totalRecords.value / rowsPerPage.value));
            if (currentPage.value > maxPage) {
                currentPage.value = maxPage;
                const ordering2 = sortField.value
                    ? (sortOrder.value === -1 ? `-${sortField.value}` : sortField.value)
                    : undefined;
                const resp2 = await loadPage({
                    page: currentPage.value,
                    rows: rowsPerPage.value,
                    search: search.value,
                    ...extra.value,
                    ...(ordering2 && { ordering: ordering2 }),
                });
                data.value = resp2?.results || [];
                totalRecords.value = Number(resp2?.count ?? 0);
            }
        } finally {
            loading.value = false;
        }
    }

    let timer = null;
    function setSearch(q) {
        search.value = q || '';
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => {
            currentPage.value = 1;
            load();
        }, debounceMs);
    }

    function setExtra(params) {
        extra.value = { ...(params || {}) };
        currentPage.value = 1;
        load();
    }

    // Пакетная установка параметров: search + extra -> одна загрузка (с дебаунсом или сразу)
    function setParams({ search: q, q: qAlias, extra: params, resetPage = true, immediate = false, delay } = {}) {
        const nextSearch = q !== undefined ? q : qAlias; // поддержка обоих имен
        if (nextSearch !== undefined) {
            search.value = nextSearch || '';
        }
        if (params !== undefined) {
            extra.value = { ...(params || {}) };
        }
        if (resetPage) currentPage.value = 1;

        if (timer) clearTimeout(timer);
        if (immediate) {
            load();
        } else {
            const d = typeof delay === 'number' ? delay : debounceMs;
            timer = setTimeout(() => {
                load();
            }, d);
        }
    }

    function onPageChange(evt) {
        if (typeof evt.rows === 'number' && evt.rows > 0) {
            rowsPerPage.value = evt.rows;
        }
        currentPage.value = typeof evt.page === 'number'
            ? evt.page + 1
            : Math.floor(evt.first / evt.rows) + 1;
        persistRows();
        load();
    }

    function onSort(evt) {
        sortField.value = evt.sortField || null;
        sortOrder.value = evt.sortOrder || null;
        currentPage.value = 1;
        load();
    }

    function firstIndex() {
        return (currentPage.value - 1) * rowsPerPage.value;
    }

    return {
        data,
        totalRecords,
        loading,
        rowsPerPage,
        currentPage,
        sortField,
        sortOrder,
        load,
        setSearch,
        setExtra,
        setParams,
        onPageChange,
        onSort,
        firstIndex,
    };
}
